import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, ArrowRight } from "lucide-react";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { FitCheck } from "@/components/site/FitCheck";
import { HeroVideoLoop } from "@/components/site/wow/HeroVideoLoop";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";

const SITE_ORIGIN = "https://fenny.se";
const PULSE_URL = `${SITE_ORIGIN}/en/pulse`;
import { SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import { aeoGeo } from "@/lib/seo/schema";
const PULSE_OG_IMAGE = SOCIAL_IMAGE_URL;

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { q: "What's included in a Pulse subscription?", a: "Script, direction, shoot, edit, grade, sound mix and format adaptation for your channels — all included in one monthly fee. You get a fixed project manager and a steady delivery rhythm where we plan each month's content together." },
    { q: "What's the commitment?", a: "We sign quarterly agreements (3 months) that roll over. To pause or end, give notice before the next period starts. No hidden fees, no hourly billing." },
    { q: "How quickly can we start?", a: "From first meeting to first shoot is normally 2–3 weeks. We run a short strategy workshop, set a content plan for the first quarter and book shoot days right away." },
    { q: "What does Pulse cost?", a: "Pulse starts from EUR 1,395/month (Pulse Lite). Pulse Core is EUR 3,195/month and Pulse Pro is EUR 4,195/month. We size the right tier for your needs at our first call — free and no commitment." },
    { q: "How do deliveries work in practice?", a: "We shoot in blocks 1–2 times per month and deliver finished films continuously in the formats you need: 16:9 for YouTube and web, 9:16 for Reels/TikTok, 1:1 for LinkedIn. Everything in a shared folder, ready to publish." },
    { q: "Can we combine Pulse with larger Prime productions?", a: "Absolutely — many clients do. Pulse keeps the steady rhythm, while Prime is used for bigger campaigns, brand films or events. Same team, same quality." },
    { q: "Do we own the material?", a: "Yes. All final material is yours to use freely on your own channels. We keep the right to show selected clips in our portfolio unless otherwise agreed." },
    { q: "Where do you shoot?", a: "We work across the Nordics and Europe. Many productions happen at your offices, on location or in our studio. Travel and logistics are included within Sweden for Pulse subscriptions." },
  ].map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Fenny Pulse",
  serviceType: "Video subscription for marketing teams",
  provider: { "@type": "Organization", name: "Fenny", url: SITE_ORIGIN },
  areaServed: ["SE", "NO", "DK", "FI", "EU"],
  description:
    "Premium video subscription. Script, production, edit, grade and format adaptation in an ongoing monthly service.",
  image: PULSE_OG_IMAGE,
  url: PULSE_URL,
};

export const Route = createFileRoute("/en_/pulse")({
  head: () => ({
    meta: [
      { title: "Pulse — Video on subscription | Fenny" },
      { name: "description", content: "Fenny Pulse is a video subscription for marketing teams that want continuous, premium-quality video content. Script, production, edit and format adaptation ." },
      { property: "og:title", content: "Pulse — Video on subscription | Fenny" },
      { property: "og:description", content: "Video on subscription for marketing teams. Script, production, edit and format adaptation in one service." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/en/pulse" },
      { property: "og:image", content: PULSE_OG_IMAGE },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Pulse — Video on subscription" },
      { name: "twitter:description", content: "Video on subscription for marketing teams." },
      { name: "twitter:image", content: PULSE_OG_IMAGE },
      { name: "language", content: "en" },
    ],
    links: [
      { rel: "canonical", href: "https://fenny.se/en/pulse" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/pulse", name: "Pulse — Video on subscription | Fenny", description: "Fenny Pulse is a video subscription for marketing teams that want continuous, premium-quality video content. Script, production, edit and format adaptation ."})) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd) },
    ],
  }),
  component: EnPulsePage,
});

const heroImages = [
  "/prime-stills/pricka-fenny-1.jpg",
  "/prime-stills/also-fenny-1.jpg",
  "/prime-stills/wimeck-fenny-1.jpg",
];
const marqueeImages = [
  "/prime-stills/garageportexperten-fenny-1.jpg",
  "/prime-stills/also-fenny-1.jpg",
  "/prime-stills/pricka-fenny-1.jpg",
  "/prime-stills/wimeck-fenny-1.jpg",
  "/prime-stills/fenny-reklam-film-1.jpg",
  "/prime-stills/elektronikbranschen-fenny-1.jpg",
  "/prime-stills/garageportexperten-fenny-3.jpg",
  "/prime-stills/raaaabaatar-fenny-1.jpg",
  "/prime-stills/bolt-fenny-1.jpg",
  "/prime-stills/garageportexperten-fenny-6.jpg",
  "/prime-stills/recompany-1.jpg",
];

const values = [
  { title: "From strategic planning to continuous production", body: "We act as a growth partner that takes ownership of the entire production." },
  { title: "Same premium quality and team every time", body: "We shoot with professional gear and obsess over light, framing and sound." },
  { title: "Deliverables shaped for your priority channels", body: "We agree the year's plan together, lock the channels and produce for those formats." },
  { title: "Transparent fixed pricing every month", body: "A predictable price model so you can budget at a lower total cost than before." },
];

const rhythm = [
  { n: "Week 1", title: "Plan", body: "We align next month's content together — themes, formats, channels and KPIs." },
  { n: "Week 2", title: "Shoot", body: "1–2 shoot days on location or in studio with the same crew you trust." },
  { n: "Week 3", title: "Edit & review", body: "First cuts in your shared folder. One round of revisions per piece." },
  { n: "Week 4", title: "Deliver & publish", body: "Final masters in every aspect ratio, ready for YouTube, LinkedIn, Reels and TikTok." },
];

const gallery = [
  { id: "GPm8ubB79DM", title: "Product of the Year 2026" },
  { id: "HwWGVOl6dtg", title: "ALSO Huawei Energy Solution in Denmark" },
  { id: "yvojXrb41Aw", title: "Bolt campaign (20 sec)" },
  { id: "KkE8BqgQ14M", title: "30 years with Garageportexperten" },
  { id: "WONrMkeJuik", title: "Wimeck Detailing — Everything in one place" },
];

function EnPulsePage() {
  const marquee = [...marqueeImages, ...marqueeImages];

  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <HeroVideoLoop images={heroImages} tone="mint" />

        <div className="mx-auto max-w-6xl px-6 pt-40 pb-24 md:pt-52 md:pb-32 lg:pt-60 lg:pb-40">
          <div className="max-w-3xl">
            <Reveal>
              <LivePulseBadge tone="mint" className="mb-6">
                Pulse · current month
              </LivePulseBadge>
            </Reveal>
            <KineticHeading
              text="Video on subscription."
              className="text-5xl font-semibold leading-[1.02] text-butter md:text-7xl lg:text-[88px]"
              highlight={{ indices: [], className: "" }}
            />
            <Reveal delay={0.5}>
              <p className="mt-8 max-w-2xl text-pretty text-lg text-foreground/90 md:text-xl">
                With Fenny Pulse you get a video subscription where you decide the content —
                commercials, social, case films and other moving media. You focus on growth,
                we run the content machine.
              </p>
            </Reveal>
            <Reveal delay={0.7}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Magnetic>
                  <Button to="/en/contact" variant="mint" arrow trackId="book_strategy_meeting" trackProps={{ location: "en_pulse_hero" }}>
                    Book a free strategy call
                  </Button>
                </Magnetic>
                <Button href="#how" variant="outline" trackId="how_pulse_works" trackProps={{ location: "en_pulse_hero" }}>
                  How Pulse works
                </Button>
                <Button href="#faq" variant="ghost">FAQ</Button>
              </div>
            </Reveal>

            <Reveal delay={0.9}>
              <dl className="mt-14 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-8">
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.18em] text-mint">Delivered 2026</dt>
                  <dd className="mt-1 text-3xl font-semibold text-foreground md:text-4xl">
                    <LiveCountUp value={247} />
                  </dd>
                  <div className="text-xs text-muted-foreground">films</div>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.18em] text-mint">Active subscriptions</dt>
                  <dd className="mt-1 text-3xl font-semibold text-foreground md:text-4xl">
                    <LiveCountUp value={38} />
                  </dd>
                  <div className="text-xs text-muted-foreground">brands</div>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-[0.18em] text-mint">Longest relationship</dt>
                  <dd className="mt-1 text-3xl font-semibold text-foreground md:text-4xl">
                    <LiveCountUp value={4} suffix=" yrs" />
                  </dd>
                  <div className="text-xs text-muted-foreground">and counting</div>
                </div>
              </dl>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section aria-label="Pulse productions" className="pulse-marquee-mask group/marquee relative overflow-hidden border-y border-white/10 py-5 md:py-6">
        <div className="pulse-marquee flex w-max gap-3 md:gap-4" aria-hidden="true">
          {marquee.map((src, i) => (
            <div key={i} className="h-32 w-[220px] shrink-0 overflow-hidden rounded-xl bg-surface md:h-48 md:w-[360px] md:rounded-2xl lg:h-56 lg:w-[420px]">
              <img src={src} alt={`Still from a Fenny Pulse video production (image ${(i % marquee.length) + 1})`} loading="lazy" decoding="async" draggable={false} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          ))}
        </div>
        <style>{`
          @keyframes pulseMarquee { from { transform: translate3d(0,0,0); } to { transform: translate3d(-50%,0,0); } }
          .pulse-marquee { animation: pulseMarquee var(--marquee-duration, 35s) linear infinite; will-change: transform; backface-visibility: hidden; }
          .group\\/marquee:hover .pulse-marquee { animation-play-state: paused; }
          .pulse-marquee-mask { -webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent); mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent); }
          @media (min-width: 768px)  { .pulse-marquee { --marquee-duration: 55s; } }
          @media (min-width: 1280px) { .pulse-marquee { --marquee-duration: 75s; } }
          @media (prefers-reduced-motion: reduce) { .pulse-marquee { animation: none; transform: none; } }
        `}</style>
      </section>

      {/* Intro + value cards */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-[1.05fr_1.25fr] lg:gap-16">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">Pulse</div>
              <h2 className="text-balance text-3xl font-semibold leading-tight text-mint md:text-4xl lg:text-[44px]">
                Content that keeps pace with your growth.
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
                <p>
                  With Fenny Pulse you skip slow processes, overloaded teams and half-finished ad-hoc fixes —
                  and you get a steady quality bar with fresh, relevant, engaging material where you decide
                  what we shoot each round.
                </p>
                <p>
                  Pulse is built for CMOs and marketing teams that want a content machine that runs without
                  hands-on management — script, production, edit, grade, format adaptation and planning all
                  solved inside one service.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button to="/en/pricing" variant="mint" arrow>See pricing</Button>
                <Button to="/en/contact" variant="outline">Talk to us</Button>
              </div>
            </Reveal>
          </div>

          <RevealStagger className="grid gap-4 sm:grid-cols-2">
            {values.map((v) => (
              <RevealItem key={v.title}>
                <TiltCard className="h-full rounded-3xl border border-white/10 bg-surface p-6 transition-colors hover:border-mint/40 md:p-7" glow="mint">
                  <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-mint/15 text-mint ring-1 ring-mint/30">
                    <Check className="h-5 w-5" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-base font-semibold leading-snug md:text-[17px]">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
                </TiltCard>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </Section>

      {/* Fit check */}
      <FitCheck
        eyebrow="Is Pulse right for you?"
        title="We don't want clients we can't deliver for."
        fitTitle="Pulse fits you if…"
        noFitTitle="Not right if…"
        fits={[
          "You need continuous video content every month across several channels",
          "You want to own a content flow instead of chasing agencies per campaign",
          "You value premium quality but don't have time to micro-manage production",
        ]}
        noFits={[
          "You need a single campaign film — look at Prime instead",
          "You want the lowest possible price rather than premium quality in a steady flow",
        ]}
        tone="mint"
      />

      {/* Gallery */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Examples"
          title="A slice of Pulse deliveries"
          lede="Five formats. Five clients. One rhythm that you set."
        />
        <RevealStagger className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {gallery.map((v, i) => (
            <RevealItem key={v.id}>
              <VideoEmbed id={v.id} title={v.title} priority={i < 3 ? "high" : "auto"} />
            </RevealItem>
          ))}
        </RevealStagger>
      </Section>

      {/* Rhythm */}
      <Section id="how">
        <EyebrowHeading
          eyebrow="How Pulse works"
          title="A predictable rhythm — week by week."
          lede="Pulse isn't ad-hoc. Every month follows the same framework so you always know what's coming."
        />
        <RevealStagger className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {rhythm.map((step) => (
            <RevealItem key={step.n}>
              <div className="h-full rounded-3xl border border-white/10 bg-surface p-7">
                <div className="text-[11px] uppercase tracking-[0.18em] text-mint">{step.n}</div>
                <h3 className="mt-3 text-xl font-semibold leading-tight">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
        <div className="mt-14 flex flex-wrap gap-3">
          <Magnetic>
            <Button to="/en/contact" variant="mint" arrow trackId="contact_us" trackProps={{ location: "en_pulse_process" }}>Contact us</Button>
          </Magnetic>
          <Button href="#faq" variant="outline">Read the FAQ</Button>
          <Button to="/en/pricing" variant="ghost">Pulse pricing</Button>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="bg-surface/30">
        <EyebrowHeading eyebrow="FAQ" title="Pulse, in plain English." />
        <div className="mx-auto mt-12 max-w-3xl divide-y divide-white/10 rounded-3xl border border-white/10 bg-surface">
          {faqJsonLd.mainEntity.map((item) => (
            <details key={item.name} className="group px-6 py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-medium text-foreground">
                {item.name}
                <ArrowRight className="h-4 w-4 shrink-0 text-mint transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.acceptedAnswer.text}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pt-0">
        <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-gradient-to-br from-surface via-surface to-mint/[0.05] p-10 text-center md:p-14">
          <h2 className="text-balance text-3xl font-semibold md:text-4xl">Ready to set the rhythm?</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Book a free 30-minute strategy call. We'll map the year together — and tell you straight
            if Pulse isn't the right match.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Magnetic>
              <Button to="/en/contact" variant="mint" arrow>Book a strategy call</Button>
            </Magnetic>
            <Link to="/en" className="inline-flex items-center gap-2 text-sm text-foreground/70 underline-offset-4 hover:text-mint hover:underline">
              Back to overview →
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
